import React, { Component } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import styles from "./SearchResultsTable.module.scss";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../common/HasPermissionTo";
import UiDateUtils from "../../utils/UiDateUtils";
import LowerCaseText from "../../utils/LowerCaseText";
import EMPObjectCard from "../../shared/components/EMPDataCard/EMPObjectCard";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import { Link } from "react-router-dom/cjs/react-router-dom";

const rolePermissionsPropType = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.string),
  PropTypes.object,
]);

const reservationPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  businessStatus: PropTypes.string,
  customerName: PropTypes.string,
  dealerName: PropTypes.string,
  modelYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nameplate: PropTypes.string,
  orderType: PropTypes.string,
  purchaseRequestDate: PropTypes.string,
  purchaseType: PropTypes.string,
});

const userPropType = PropTypes.shape({
  dealerGroup: PropTypes.arrayOf(
    PropTypes.shape({
      commonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      regionalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  permissions: PropTypes.shape({
    rolePermissions: rolePermissionsPropType.isRequired,
  }).isRequired,
});

class ReservationResult extends Component {
  determineRegionalId = () => {
    const { commonDealerId, user } = this.props;

    const filteredDealer = user.dealerGroup?.find(
      (dealer) => dealer.commonId === commonDealerId
    );

    return filteredDealer?.regionalId;
  };

  translateOrderType = () => {
    const { reservation, t } = this.props;

    if (reservation.purchaseType || reservation.purchaseRequestDate) {
      return t("CustomerReservations.purchaseRequest");
    }

    if (LowerCaseText(reservation.orderType).includes("order")) {
      return t("CustomerReservations.order");
    }

    return reservation.orderType;
  };

  renderRegionalId = () => {
    const regionalId = this.determineRegionalId();

    if (!regionalId) {
      return null;
    }

    return <span className={styles.pre}>{`${regionalId}  |  `}</span>;
  };

  renderViewDetailsButton = () => {
    const { commonDealerId, reservation, showOrderDetails, t } = this.props;

    return (
      <EMPButton
        variant="text"
        id="viewDetailsButton"
        testId="crcView"
        buttonElement="button"
        onClick={() => showOrderDetails(commonDealerId, reservation.id, true)}
      >
        {t("CrcOrderSearch.viewDetails")}
      </EMPButton>
    );
  };

  renderReservationLink = () => {
    const { reservation, t } = this.props;

    return (
      <Link
        id="reservationLink"
        to={`/customer-handling/${reservation.id}`}
        className={styles.tableLink}
      >
        {t("CustomerReservations.view")}
      </Link>
    );
  };

  render() {
    const { reservation, user } = this.props;

    return (
      <>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          columnGap={4}
          rowGap={4}
          alignItems="center"
        >
          <Box gridColumn={{ xs: "span 2" }}>
            <HasPermissionTo
              perform={["reservationsRegionalId:view"]}
              permissions={user.permissions.rolePermissions}
              render={this.renderRegionalId}
            />
            <Typography variant="h4">{reservation.id}</Typography>
          </Box>
          <Box gridColumn={{ xs: "span 3" }}>
            <HasPermissionTo
              perform={["crcOrderDetails:view"]}
              permissions={user.permissions.rolePermissions}
              render={this.renderViewDetailsButton}
              noRender={this.renderReservationLink}
            />
          </Box>
        </Box>
        <Box
          gridColumn={{
            xs: "span 12",
          }}
        >
          <EMPObjectCard
            data={{
              customerName: {
                heading: "Customer Name",
                content: reservation.customerName || "--",
              },
              dealerName: {
                heading: "Dealer Name",
                content: reservation.dealerName || "--",
              },
              vehicle: {
                heading: "Vehicle",
                content: `${reservation.modelYear} ${reservation.nameplate}`,
              },
              date: {
                heading: "Date",
                content: UiDateUtils.formattedDate(
                  reservation.purchaseRequestDate
                ),
              },
              orderType: {
                heading: "Order Type",
                content: this.translateOrderType(),
              },
              status: {
                heading: "Status",
                content: reservation.businessStatus,
              },
            }}
          />
        </Box>
      </>
    );
  }
}

ReservationResult.propTypes = {
  commonDealerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  reservation: reservationPropType.isRequired,
  showOrderDetails: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: userPropType.isRequired,
};

class SearchResultsTable extends Component {
  render() {
    const { commonDealerId, reservations, showOrderDetails, t, user } =
      this.props;

    return (
      <div className={styles.tablePadding}>
        {reservations.map((reservation) => (
          <ReservationResult
            key={reservation.id}
            commonDealerId={commonDealerId}
            reservation={reservation}
            showOrderDetails={showOrderDetails}
            t={t}
            user={user}
          />
        ))}
      </div>
    );
  }
}

SearchResultsTable.propTypes = {
  commonDealerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  reservations: PropTypes.arrayOf(reservationPropType).isRequired,
  showOrderDetails: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: userPropType.isRequired,
};

export default withTranslation("emp")(SearchResultsTable);
